import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { fetchFicheAssistance } from "../../../store/actions/assistancesActions"
import { AssistancesProvider, useAssistances } from "./AssistancesContext"
import { BandeauOmbre, Mono, RoleChip, montant, ui } from "./assistancesUi"

// Fiche dépannage V2 (GE-57, spec §11.5) : bloc « Assistance » compact
// (ordres, montant accordé, lien vers le panneau Dossier) + n° d'ordre par
// livraison (badge dans DepFicheTrajetV2). Une seule requête pour les deux :
// cache court par fiche.
const cache = new Map()
const DUREE_CACHE_MS = 5000

const charger = (dispatch, depId) => {
	const entree = cache.get(depId)
	if (entree && Date.now() - entree.t < DUREE_CACHE_MS) return entree.promesse
	const promesse = dispatch(fetchFicheAssistance(depId))
	cache.set(depId, { promesse, t: Date.now() })
	return promesse
}

export const useFicheAssistance = (depId) => {
	const dispatch = useDispatch()
	const ctx = useAssistances()
	const revision = ctx ? ctx.revision : 0
	const [data, setData] = useState(null)
	useEffect(() => {
		let actif = true
		// Feuilletage : pas de données de la fiche précédente (GE-59).
		setData(null)
		if (!depId) return undefined
		if (revision) cache.delete(depId)
		charger(dispatch, depId).then((d) => {
			if (actif) setData(d || null)
		})
		return () => {
			actif = false
		}
	}, [dispatch, depId, revision])
	return data
}

// Badge d'ordre d'une livraison (ex. « A-451357 »), rien sans ordre.
export const BadgeOrdreLivraison = ({ depId, livraisonId }) => {
	const data = useFicheAssistance(depId)
	const ref = data && data.livraisons && data.livraisons[livraisonId]
	if (!ref) return null
	return (
		<span
			style={st.badge}
			title="Ordre de l'assistance à reporter sur sa facture"
		>
			{ref}
		</span>
	)
}

// Ordres GE-57 des dossiers de la fiche (Accordé / Facturé, lien vers le
// panneau Dossier). GE-60 : rendu DANS la carte « Couvertures », qui remplace
// l'ancien bloc « Assistance » et en reprend les ordres.
export const OrdresAssistance = ({ data }) => {
	const ctx = useAssistances()
	return (
		<div>
			{data.mode === "ombre" && <BandeauOmbre court />}
			{data.dossiers.map((d) => (
				<div key={`dossier_${d.id}`} style={{ marginBottom: 10 }}>
					<div style={{ ...st.kv, ...st.entete }}>
						<span>Ordre</span>
						<span style={st.num}>Accordé</span>
						<span style={st.num}>Facturé</span>
					</div>
					{d.ordres.map((o) => (
						<div
							key={`o_${o.id}`}
							style={o.clos ? { ...st.kv, opacity: 0.55 } : st.kv}
							title={
								o.clos
									? `Ordre ${o.statut_label.toLowerCase()}`
									: undefined
							}
						>
							<span
								style={{
									display: "flex",
									gap: 6,
									alignItems: "center",
									flexWrap: "wrap",
								}}
							>
								<Mono>{o.ref_ordre || o.role_label}</Mono>
								<RoleChip role={o.role} />
							</span>
							<span
								style={{
									...st.num,
									color:
										o.montant === null
											? COLORS.textMuted
											: COLORS.text,
								}}
							>
								{montant(o.montant, o.devise)}
							</span>
							<span
								style={{ ...st.num, color: COLORS.textMuted }}
							>
								{o.facture ? Number(o.facture).toFixed(2) : "—"}
							</span>
						</div>
					))}
					<div style={st.pied}>
						<span style={{ fontSize: 13, color: COLORS.textMuted }}>
							{d.assistance} · réf. {d.ref_affaire}
						</span>
						<button
							type="button"
							style={{ ...ui.lien, fontSize: 13 }}
							onClick={() => ctx.ouvrirDossier(d.id)}
						>
							Voir le dossier →
						</button>
					</div>
				</div>
			))}
		</div>
	)
}

// Le provider enveloppe tout : le hook lit `revision` et le bloc se recharge
// après une action faite dans le panneau Dossier (GE-59).
const BlocSiDossier = ({ depId }) => {
	const data = useFicheAssistance(depId)
	if (!data || !data.dossiers || !data.dossiers.length) return null
	return (
		<div style={gs.card}>
			<div style={gs.cardTitle}>Assistance</div>
			<OrdresAssistance data={data} />
		</div>
	)
}

const FicheAssistanceBloc = ({ depId }) =>
	depId ? (
		<AssistancesProvider>
			<BlocSiDossier depId={depId} />
		</AssistancesProvider>
	) : null

const st = {
	kv: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1fr) auto auto",
		gap: 10,
		fontSize: 13,
		alignItems: "center",
		padding: "8px 0",
		borderBottom: `1px solid ${COLORS.border}`,
	},
	entete: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
	},
	num: {
		textAlign: "right",
		minWidth: 80,
		fontVariantNumeric: "tabular-nums",
	},
	pied: {
		marginTop: 12,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: 10,
		flexWrap: "wrap",
	},
	badge: {
		marginLeft: 8,
		fontSize: 11,
		fontWeight: 700,
		color: "#0F766E",
		backgroundColor: "#CCFBF1",
		borderRadius: 99,
		padding: "2px 9px",
		whiteSpace: "nowrap",
	},
}

export default FicheAssistanceBloc
